import React, { useState, useRef, useEffect } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import axios from 'axios';

const BASE_URL = 'https://whapi.infosware-test.in'; // Define base URL

function ScanButton() {
    const [inputValue, setInputValue] = useState('');
    const [buffer, setBuffer] = useState('');
    const [isScanning, setIsScanning] = useState(true); // Start with scanning active
    const [scannedIds, setScannedIds] = useState(new Set()); // Tracks unique IDs
    const [discrepancies, setDiscrepancies] = useState({ missing: [], extra: [], present: [] }); // Holds discrepancies
    const [showResults, setShowResults] = useState(false); // Show the results view
    const inputRef = useRef(null);

    const handleStart = () => {
        setIsScanning(true);
        setShowResults(false);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleStop = () => {
        setIsScanning(false);
        if (buffer.trim()) {
            saveToDatabase(Array.from(scannedIds)); // Send unique scanned data to the database
        }
    };

    const handleSave = async () => {
        try {
            // Ensure the data is being sent as expected
            console.log('Sending data:', discrepancies);

            const response = await axios.post(`${BASE_URL}/scans/save-report`, {
                missing: discrepancies.missing, // Send separate fields
                extra: discrepancies.extra,
                present: discrepancies.present
            });

            if (response.status === 200) {
                console.log('Discrepancies saved successfully:', response.data);
                alert('Discrepancies saved successfully!');
                // Reset state and show scan view again
                setDiscrepancies({ missing: [], extra: [], present: [] });
                setScannedIds(new Set());
                setBuffer('');
                setShowResults(false);
                setIsScanning(true);
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }
        } catch (error) {
            console.error('Error saving discrepancies:', error);
            alert('Failed to save discrepancies!');
        }
    };

    const handleChange = (e) => {
        const value = e.target.value;
        setInputValue(value);

        if (value.length === 24) {
            // Check for duplicate IDs
            if (!scannedIds.has(value)) {
                setBuffer((prevBuffer) => prevBuffer + value + '\n');
                setScannedIds((prevSet) => new Set(prevSet).add(value)); // Add to scanned IDs
            }
            // Reset input for rescanning (regardless of whether it's a duplicate)
            setInputValue(''); // Clear the input for the next scan
        }
    };

    // Function to save scanned data to the database and compare with tags
    const saveToDatabase = async (scannedData) => {
        try {
            // Ensure that scannedData is in the correct format (an array of strings or IDs)
            console.log("Sending scanned data:", scannedData);

            // Sending the scanned data to the backend for saving
            const saveResponse = await axios.post(`${BASE_URL}/scans`, { data: scannedData });
            console.log('Save response:', saveResponse.data);

            // After saving, compare IDs from scans table with tags table
            const compareResponse = await axios.post(`${BASE_URL}/scans/compare-tags`, { scannedData });
            console.log('Compare response:', compareResponse.data);

            const { missingTags, extraTags, presentTags } = compareResponse.data;

            // Set discrepancies state
            setDiscrepancies({ missing: missingTags, extra: extraTags, present: presentTags });

            // Show results view
            setShowResults(true);
        } catch (error) {
            console.error("Error saving data:", error);
            alert("Failed to save data!");
        }
    };

    useEffect(() => {
        if (isScanning && inputRef.current) {
            inputRef.current.focus(); // Automatically focus input when scanning
        }
    }, [isScanning]);

    return (
        <div>
            {!showResults && (
                <div className='mx-2 mt-4'>
                    <Form>
                        <Form.Group controlId="scanInput">
                            <Form.Label>Enter ID :</Form.Label>
                            <Form.Control
                                type="text"
                                value={inputValue}
                                onChange={handleChange}
                                ref={inputRef}
                                placeholder="Enter 24-digit ID"
                                disabled={!isScanning} // Disable input when not scanning
                                className="scan-input mb-2"
                            />
                        </Form.Group>
                        <div>
                            <strong>Buffered Data:</strong>
                            <pre>{buffer}</pre>
                        </div>
                    </Form>
                    <Button variant="success" className='me-2' onClick={handleStart} disabled={isScanning}>
                        Start
                    </Button>
                    <Button variant="danger" onClick={handleStop} disabled={!isScanning}>
                        Stop
                    </Button>
                </div>
            )}

            {showResults && (
                <div>
                    <div>
                        <h5>Present Tags:</h5>
                        {discrepancies.present.length > 0 ? (
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th>Item Name</th>
                                        <th>Present Tags Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {discrepancies.present.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.item_name}</td>
                                            <td>{item.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p>No present tags</p>
                        )}
                    </div>
                    <div>
                        <h5>Missing Tags:</h5>
                        {discrepancies.missing.length > 0 ? (
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th>Item Name</th>
                                        <th>Missing Tags Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {discrepancies.missing.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.item_name}</td>
                                            <td>{item.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p>No missing tags</p>
                        )}
                    </div>
                    <div>
                        <h5>Extra Tags:</h5>
                        {discrepancies.extra.length > 0 ? (
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th>Item Name</th>
                                        <th>Extra Tags Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {discrepancies.extra.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.item_name}</td>
                                            <td>{item.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p>No extra tags</p>
                        )}
                    </div>
                    <Button variant="primary" onClick={handleSave}>
                        Save
                    </Button>
                </div>
            )}

            <style jsx>{`
                /* Input Style */
                .scan-input {
                    border-radius: 8px; /* Rounded corners for input */
                    padding: 10px;
                    font-size: 1rem;
                    border: 1px solid #ccc; /* Light gray border */
                }

                /* Table Styling */
                .table-striped tbody tr:nth-of-type(odd) {
                    background-color: #f2f2f2; /* Light gray background for odd rows */
                }

                .table th, .table td {
                    padding: 12px; /* More padding for the table cells */
                    text-align: center; /* Center-align text */
                    font-size: 1rem; /* Font size adjustment */
                }

                .table th {
                    background-color: #007bff; /* Blue background for table header */
                    color: white;
                }
            `}</style>
        </div>
    );
}

export default ScanButton;
